import { useEffect, useRef, useState } from 'react';
import {
  createDPCMNode,
  createEngine,
  createNoiseNode,
  createPulseNode,
  createTriangleNode,
  DEMO_SONG,
  generateKick,
  generateSnare,
  getAudioContext,
  loadDPCMWorklet,
  loadNoiseWorklet,
  loadPulseWorklet,
  loadTriangleWorklet,
  noteToFrequency,
  type DPCMNode,
  type EngineHandle,
  type NoiseMode,
  type NoiseNode,
  type PulseDuty,
  type PulseNode,
  type TriangleNode,
} from '../audio';

const NOTES = [
  'C-2',
  'C-3',
  'C-4',
  'D-4',
  'E-4',
  'F-4',
  'G-4',
  'A-4',
  'B-4',
  'C-5',
  'D-5',
  'E-5',
  'F-5',
  'G-5',
  'A-5',
  'B-5',
  'C-6',
] as const;

const DUTY_LABELS: Record<PulseDuty, string> = {
  0: '12.5% (thin)',
  1: '25% (classic lead)',
  2: '50% (square)',
  3: '75% (fat)',
};

const NOISE_PRESETS: Record<string, { periodIndex: number; mode: NoiseMode; description: string }> =
  {
    'hi-hat': { periodIndex: 2, mode: 'long', description: 'High white noise — hi-hat / shaker' },
    snare: { periodIndex: 5, mode: 'long', description: 'Mid white noise — snare body' },
    'low rumble': { periodIndex: 12, mode: 'long', description: 'Low white noise — kick body / rumble' },
    'metallic ping': {
      periodIndex: 4,
      mode: 'short',
      description: 'Short-mode = pitched/metallic — SFX',
    },
    'sub buzz': {
      periodIndex: 14,
      mode: 'short',
      description: 'Short mode at low rate — robot buzz',
    },
  };

export function AudioTest() {
  const pulseRef = useRef<PulseNode | null>(null);
  const triangleRef = useRef<TriangleNode | null>(null);
  const noiseRef = useRef<NoiseNode | null>(null);
  const dpcmRef = useRef<DPCMNode | null>(null);
  const dpcmSamplesLoaded = useRef(false);

  // Pulse
  const [pulseNote, setPulseNote] = useState<string>('C-5');
  const [pulseDuty, setPulseDuty] = useState<PulseDuty>(1);
  const [pulseVolume, setPulseVolume] = useState<number>(12);
  const [pulsePlaying, setPulsePlaying] = useState(false);

  // Triangle
  const [triangleNote, setTriangleNote] = useState<string>('C-3');
  const [trianglePlaying, setTrianglePlaying] = useState(false);

  // Noise
  const [noisePeriod, setNoisePeriod] = useState<number>(5);
  const [noiseMode, setNoiseMode] = useState<NoiseMode>('long');
  const [noiseVolume, setNoiseVolume] = useState<number>(12);
  const [noisePlaying, setNoisePlaying] = useState(false);

  // DPCM
  const [dpcmRateIndex, setDpcmRateIndex] = useState<number>(11);
  const [dpcmVolume, setDpcmVolume] = useState<number>(13);

  // Engine + demo playback
  const engineRef = useRef<EngineHandle | null>(null);
  const [songPlaying, setSongPlaying] = useState(false);
  const [playhead, setPlayhead] = useState<{ orderIndex: number; rowInPattern: number } | null>(
    null,
  );
  const [songTempo, setSongTempo] = useState<number>(150);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      pulseRef.current?.destroy();
      triangleRef.current?.destroy();
      noiseRef.current?.destroy();
      dpcmRef.current?.destroy();
      engineRef.current?.destroy();
      pulseRef.current = null;
      triangleRef.current = null;
      noiseRef.current = null;
      dpcmRef.current = null;
      engineRef.current = null;
    };
  }, []);

  // Poll the engine playhead while a song is playing so the UI reflects position.
  useEffect(() => {
    if (!songPlaying) return;
    const id = window.setInterval(() => {
      const eng = engineRef.current;
      if (!eng) return;
      if (!eng.isPlaying()) {
        setSongPlaying(false);
        setPlayhead(null);
        return;
      }
      setPlayhead(eng.getPlayhead());
    }, 50);
    return () => window.clearInterval(id);
  }, [songPlaying]);

  // Live parameter updates while a note is sustained.
  useEffect(() => {
    if (!pulsePlaying || !pulseRef.current) return;
    pulseRef.current.setFrequency(noteToFrequency(pulseNote));
    pulseRef.current.setDuty(pulseDuty);
    pulseRef.current.setVolume(pulseVolume);
  }, [pulsePlaying, pulseNote, pulseDuty, pulseVolume]);

  useEffect(() => {
    if (!trianglePlaying || !triangleRef.current) return;
    triangleRef.current.setFrequency(noteToFrequency(triangleNote));
  }, [trianglePlaying, triangleNote]);

  useEffect(() => {
    if (!noisePlaying || !noiseRef.current) return;
    noiseRef.current.setPeriodIndex(noisePeriod);
    noiseRef.current.setMode(noiseMode);
    noiseRef.current.setVolume(noiseVolume);
  }, [noisePlaying, noisePeriod, noiseMode, noiseVolume]);

  useEffect(() => {
    if (!dpcmRef.current) return;
    dpcmRef.current.setRateIndex(dpcmRateIndex);
    dpcmRef.current.setVolume(dpcmVolume);
  }, [dpcmRateIndex, dpcmVolume]);

  async function togglePulse() {
    try {
      setError(null);
      if (pulsePlaying) {
        pulseRef.current?.noteOff();
        setPulsePlaying(false);
        return;
      }
      const ctx = await getAudioContext();
      await loadPulseWorklet(ctx);
      if (!pulseRef.current) pulseRef.current = createPulseNode(ctx);
      const node = pulseRef.current;
      node.setFrequency(noteToFrequency(pulseNote));
      node.setDuty(pulseDuty);
      node.setVolume(pulseVolume);
      node.noteOn();
      setPulsePlaying(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setPulsePlaying(false);
    }
  }

  async function toggleTriangle() {
    try {
      setError(null);
      if (trianglePlaying) {
        triangleRef.current?.noteOff();
        setTrianglePlaying(false);
        return;
      }
      const ctx = await getAudioContext();
      await loadTriangleWorklet(ctx);
      if (!triangleRef.current) triangleRef.current = createTriangleNode(ctx);
      const node = triangleRef.current;
      node.setFrequency(noteToFrequency(triangleNote));
      node.noteOn();
      setTrianglePlaying(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setTrianglePlaying(false);
    }
  }

  async function toggleNoise() {
    try {
      setError(null);
      if (noisePlaying) {
        noiseRef.current?.noteOff();
        setNoisePlaying(false);
        return;
      }
      const ctx = await getAudioContext();
      await loadNoiseWorklet(ctx);
      if (!noiseRef.current) noiseRef.current = createNoiseNode(ctx);
      const node = noiseRef.current;
      node.setPeriodIndex(noisePeriod);
      node.setMode(noiseMode);
      node.setVolume(noiseVolume);
      node.noteOn();
      setNoisePlaying(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setNoisePlaying(false);
    }
  }

  function applyNoisePreset(name: string) {
    const preset = NOISE_PRESETS[name];
    if (!preset) return;
    setNoisePeriod(preset.periodIndex);
    setNoiseMode(preset.mode);
  }

  async function ensureDpcm() {
    const ctx = await getAudioContext();
    await loadDPCMWorklet(ctx);
    if (!dpcmRef.current) {
      dpcmRef.current = createDPCMNode(ctx);
      dpcmRef.current.setRateIndex(dpcmRateIndex);
      dpcmRef.current.setVolume(dpcmVolume);
    }
    if (!dpcmSamplesLoaded.current) {
      const kick = generateKick();
      const snare = generateSnare();
      dpcmRef.current.loadSample('kick', kick.data, kick.sampleRate);
      dpcmRef.current.loadSample('snare', snare.data, snare.sampleRate);
      dpcmSamplesLoaded.current = true;
    }
    return dpcmRef.current;
  }

  async function triggerDPCMSample(name: 'kick' | 'snare') {
    try {
      setError(null);
      const node = await ensureDpcm();
      node.trigger(name, false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function toggleSong() {
    try {
      setError(null);
      if (songPlaying) {
        engineRef.current?.stop();
        setSongPlaying(false);
        setPlayhead(null);
        return;
      }
      if (!engineRef.current) engineRef.current = createEngine();
      await engineRef.current.playDemo(DEMO_SONG, 6, songTempo);
      setSongPlaying(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setSongPlaying(false);
    }
  }

  return (
    <section className="mt-6 flex w-full max-w-md flex-col gap-4">
      <h2 className="text-base font-bold text-nes-cyan">Audio test — channels</h2>

      <ChannelPanel title="Demo song (scheduler + all channels)" color="cyan">
        <Row label="Tempo">
          <input
            type="range"
            min={80}
            max={220}
            value={songTempo}
            onChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setSongTempo(next);
              engineRef.current?.setTempo(6, next);
            }}
            className="flex-1 accent-nes-cyan"
          />
          <span className="w-12 text-right text-xs text-nes-white/60">{songTempo} BPM</span>
        </Row>
        <p className="text-xs text-nes-white/40">
          {DEMO_SONG.title} — 16-row patterns, i-VI-VII-i in A minor. Plays pulse 1 melody +
          triangle bass + noise hi-hat + DPCM kick/snare. Tempo locked while playing (Phase 1
          step 4 limitation).
        </p>
        {playhead && (
          <p className="text-xs text-nes-cyan/80">
            ▶ order {playhead.orderIndex} · pattern row {playhead.rowInPattern}
          </p>
        )}
        <ToggleButton
          playing={songPlaying}
          onClick={toggleSong}
          idleClass="bg-nes-cyan text-nes-black hover:bg-nes-cyan/80"
          label={`demo @ ${songTempo} BPM`}
        />
      </ChannelPanel>

      <ChannelPanel title="Pulse (lead / harmony)" color="cyan">
        <Row label="Note">
          <Select value={pulseNote} onChange={setPulseNote} options={NOTES} />
        </Row>
        <Row label="Duty">
          <Select
            value={String(pulseDuty)}
            onChange={(v) => setPulseDuty(parseInt(v, 10) as PulseDuty)}
            options={(Object.entries(DUTY_LABELS) as [string, string][]).map(([k, l]) => ({
              value: k,
              label: l,
            }))}
          />
        </Row>
        <Row label="Volume">
          <input
            type="range"
            min={0}
            max={15}
            value={pulseVolume}
            onChange={(e) => setPulseVolume(parseInt(e.target.value, 10))}
            className="flex-1 accent-nes-cyan"
          />
          <span className="w-8 text-right text-xs text-nes-white/60">{pulseVolume}</span>
        </Row>
        <ToggleButton
          playing={pulsePlaying}
          onClick={togglePulse}
          idleClass="bg-nes-cyan text-nes-black hover:bg-nes-cyan/80"
          label={pulseNote}
        />
      </ChannelPanel>

      <ChannelPanel title="Triangle (bass)" color="white">
        <Row label="Note">
          <Select value={triangleNote} onChange={setTriangleNote} options={NOTES} />
        </Row>
        <p className="text-xs text-nes-white/40">
          No volume — NES triangle is binary on/off. The quantized 32-step sequence gives the
          characteristic buzzy chiptune bass.
        </p>
        <ToggleButton
          playing={trianglePlaying}
          onClick={toggleTriangle}
          idleClass="bg-nes-white text-nes-black hover:bg-nes-white/80"
          label={triangleNote}
        />
      </ChannelPanel>

      <ChannelPanel title="Noise (drums / SFX)" color="red">
        <Row label="Preset">
          <Select
            value=""
            onChange={applyNoisePreset}
            options={[
              { value: '', label: '— pick one —' },
              ...Object.keys(NOISE_PRESETS).map((k) => ({ value: k, label: k })),
            ]}
          />
        </Row>
        <Row label="Mode">
          <div className="flex gap-2">
            <ModeButton
              active={noiseMode === 'long'}
              onClick={() => setNoiseMode('long')}
              label="long (white)"
            />
            <ModeButton
              active={noiseMode === 'short'}
              onClick={() => setNoiseMode('short')}
              label="short (metallic)"
            />
          </div>
        </Row>
        <Row label="Period">
          <input
            type="range"
            min={0}
            max={15}
            value={noisePeriod}
            onChange={(e) => setNoisePeriod(parseInt(e.target.value, 10))}
            className="flex-1 accent-nes-red"
          />
          <span className="w-8 text-right text-xs text-nes-white/60">{noisePeriod}</span>
        </Row>
        <Row label="Volume">
          <input
            type="range"
            min={0}
            max={15}
            value={noiseVolume}
            onChange={(e) => setNoiseVolume(parseInt(e.target.value, 10))}
            className="flex-1 accent-nes-red"
          />
          <span className="w-8 text-right text-xs text-nes-white/60">{noiseVolume}</span>
        </Row>
        <p className="text-xs text-nes-white/40">
          Period 0 = LFSR ticks at ~447 kHz (high-frequency hiss). Period 15 = ~440 Hz tick rate
          (low rumble). Long mode = white noise (32 767-bit sequence), short mode = pitched
          metallic noise (93-bit sequence).
        </p>
        <ToggleButton
          playing={noisePlaying}
          onClick={toggleNoise}
          idleClass="bg-nes-red text-nes-white hover:bg-nes-red/80"
          label={`period ${noisePeriod} · ${noiseMode}`}
        />
      </ChannelPanel>

      <ChannelPanel title="DPCM (sample-based drums)" color="cyan">
        <Row label="Rate">
          <input
            type="range"
            min={0}
            max={15}
            value={dpcmRateIndex}
            onChange={(e) => setDpcmRateIndex(parseInt(e.target.value, 10))}
            className="flex-1 accent-nes-cyan"
          />
          <span className="w-8 text-right text-xs text-nes-white/60">{dpcmRateIndex}</span>
        </Row>
        <Row label="Volume">
          <input
            type="range"
            min={0}
            max={15}
            value={dpcmVolume}
            onChange={(e) => setDpcmVolume(parseInt(e.target.value, 10))}
            className="flex-1 accent-nes-cyan"
          />
          <span className="w-8 text-right text-xs text-nes-white/60">{dpcmVolume}</span>
        </Row>
        <div className="flex gap-2">
          <button
            onClick={() => triggerDPCMSample('kick')}
            className="rounded bg-nes-cyan px-4 py-2 text-sm font-bold text-nes-black transition hover:bg-nes-cyan/80"
          >
            ▶ Kick
          </button>
          <button
            onClick={() => triggerDPCMSample('snare')}
            className="rounded bg-nes-cyan px-4 py-2 text-sm font-bold text-nes-black transition hover:bg-nes-cyan/80"
          >
            ▶ Snare
          </button>
        </div>
        <p className="text-xs text-nes-white/40">
          Synthesized from scratch — no rips. Rate index changes playback speed: lower index =
          slower / lower pitch, higher index = faster / higher pitch (NES rate table, not Hz).
          One-shot trigger — each click plays from the start.
        </p>
      </ChannelPanel>

      {error && <p className="text-sm text-nes-red">Error: {error}</p>}

      <p className="text-xs text-nes-white/40">
        All three channels can run in parallel — try triangle bass + pulse melody + noise hi-hat
        to hear a rough chiptune mix. Parameters update live while notes are held.
      </p>
    </section>
  );
}

function ChannelPanel({
  title,
  color,
  children,
}: {
  title: string;
  color: 'cyan' | 'white' | 'red';
  children: React.ReactNode;
}) {
  const borderClass =
    color === 'cyan' ? 'border-nes-cyan/30' : color === 'red' ? 'border-nes-red/30' : 'border-nes-white/30';
  const titleClass =
    color === 'cyan' ? 'text-nes-cyan' : color === 'red' ? 'text-nes-red' : 'text-nes-white';
  return (
    <div className={`flex flex-col gap-3 rounded border ${borderClass} bg-nes-dim p-4`}>
      <h3 className={`text-sm font-bold ${titleClass}`}>{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <label className="w-16 text-sm text-nes-white/70">{label}:</label>
      {children}
    </div>
  );
}

type Option = string | { value: string; label: string };
function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly Option[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded border border-nes-cyan/30 bg-nes-black px-2 py-1 text-sm text-nes-cyan"
    >
      {options.map((opt) => {
        const o = typeof opt === 'string' ? { value: opt, label: opt } : opt;
        return (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        );
      })}
    </select>
  );
}

function ToggleButton({
  playing,
  onClick,
  idleClass,
  label,
}: {
  playing: boolean;
  onClick: () => void;
  idleClass: string;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`self-start rounded px-4 py-2 text-sm font-bold transition ${
        playing ? 'bg-nes-red text-nes-white hover:bg-nes-red/80' : idleClass
      }`}
    >
      {playing ? `■ Stop (${label})` : `▶ Play ${label}`}
    </button>
  );
}

function ModeButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded border px-2 py-1 text-xs transition ${
        active
          ? 'border-nes-red bg-nes-red/20 text-nes-white'
          : 'border-nes-white/20 text-nes-white/60 hover:border-nes-white/40'
      }`}
    >
      {label}
    </button>
  );
}
